import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'context/AuthContext';
import { canAction } from 'permissions';
import api, { urlDocument } from 'services/api';
import ImageProtegee from 'components/ui/ImageProtegee';
import useEscapeClose from 'hooks/useEscapeClose';
import IAAnalysisPanel from 'components/shared/IAAnalysisPanel';
import { preparerFichier } from 'services/televersement';


const TYPES = [
  { value: 'intrusion', label: 'Intrusion' },
  { value: 'vol', label: 'Vol' },
  { value: 'accident', label: 'Accident' },
  { value: 'incendie', label: 'Incendie' },
  { value: 'bagarre', label: 'Bagarre' },
  { value: 'vandalisme', label: 'Vandalisme' },
  { value: 'tentative_vol', label: 'Tentative de vol' },
  { value: 'agression', label: 'Agression' },
  { value: 'autre', label: 'Autre' },
];

const GRAVITES = [
  { value: 'mineure', label: 'Mineure', color: '#9CA3AF', badge: 'info' },
  { value: 'moyenne', label: 'Moyenne', color: '#3B82F6', badge: 'info' },
  { value: 'majeure', label: 'Majeure', color: '#F97316', badge: 'warning' },
  { value: 'critique', label: 'Critique', color: '#EF4444', badge: 'danger' },
];

const STATUTS = ['ouvert', 'en_cours', 'clos'];

export default function Incidents() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState({});
  const [sites, setSites] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({ statut: '', type_incident: '', gravite: '', site_id: '', search: '', date_debut: '', date_fin: '' });
  const [form, setForm] = useState({
    type_incident: '', description: '', gravite: 'moyenne',
    mesures_prises: '', police_alertee: false, site_id: '', agent_id: '',
  });
  const [photos, setPhotos] = useState([]);
  const [erreurPhotos, setErreurPhotos] = useState('');

  /* Chaque photo est contrôlée ET redimensionnée avant d'entrer dans l'état.
   * ⚠️ UNE PHOTO REFUSÉE EST NOMMÉE, PAS SAUTÉE EN SILENCE : sur 4 fichiers
   *    choisis, si 1 est refusé, l'utilisateur doit savoir lequel et pourquoi —
   *    sinon il envoie son incident en croyant avoir joint 4 photos. */
  const choisirPhotos = async (e) => {
    const choisis = [...e.target.files];
    if (!choisis.length) return;
    const retenues = [];
    const refus = [];
    for (const f of choisis) {
      const { fichier, erreur } = await preparerFichier(f, 'photo_incident');
      if (erreur) refus.push(`${f.name} : ${erreur}`);
      else retenues.push(fichier);
    }
    setPhotos(retenues);
    setErreurPhotos(refus.length
      ? `${refus.length} photo(s) non retenue(s) — ${refus.join(' · ')}`
      : '');
  };
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [incRes, statsRes, sitesRes, agentsRes] = await Promise.all([
        api.get('/incidents', { params }),
        api.get('/incidents/stats'),
        api.get('/sites').catch(() => null),
        api.get('/agents').catch(() => null),
      ]);
      setIncidents(incRes.data);
      setStats(statsRes.data);
      if (sitesRes) setSites(sitesRes.data);
      if (agentsRes) setAgents(agentsRes.data);
    } catch {}
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.type_incident || !form.description) return alert('Type et description requis');
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach(f => fd.append('photos', f));

      await api.post('/incidents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowForm(false);
      setForm({ type_incident: '', description: '', gravite: 'moyenne', mesures_prises: '', police_alertee: false, site_id: '', agent_id: '' });
      setPhotos([]);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur de connexion');
    }
    setLoading(false);
  };

  const voirDetail = async (id) => {
    try {
      const res = await api.get(`/incidents/${id}`);
      setDetail(res.data);
    } catch {}
  };

  const updateStatut = async (id, statut) => {
    try {
      await api.put(`/incidents/${id}`, { statut });
      load();
      if (detail?.id === id) voirDetail(id);
    } catch {}
  };

  const telechargerPDF = async (id) => {
    try {
      const res = await api.get(`/incidents/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {}
  };

  useEscapeClose(showForm, () => setShowForm(false));
  useEscapeClose(detail && !showForm, () => setDetail(null));

  const graviteInfo = (g) => GRAVITES.find(v => v.value === g) || { color: '#6B7280', badge: 'info', label: g };
  const statutBadge = (s) => {
    const colors = { ouvert: 'danger', en_cours: 'warning', clos: 'success' };
    return colors[s] || 'info';
  };

  return (
    <div className="page-container">
<div className="stats-cards">
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Total incidents</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div></div><div className="stat-value">{stats.total || 0}</div></div>
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Ouverts</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg></div></div><div className="stat-value" style={{ color: '#EF4444' }}>{stats.ouvert || 0}</div></div>
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">En cours</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4"/><path d="M12 18v4"/><path d="M4.93 4.93l2.83 2.83"/><path d="M16.24 16.24l2.83 2.83"/><path d="M2 12h4"/><path d="M18 12h4"/><path d="M4.93 19.07l2.83-2.83"/><path d="M16.24 7.76l2.83-2.83"/></svg></div></div><div className="stat-value" style={{ color: '#F59E0B' }}>{stats.en_cours || 0}</div></div>
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Clos</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div></div><div className="stat-value" style={{ color: '#22C55E' }}>{stats.clos || 0}</div></div>
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Aujourd'hui</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div></div><div className="stat-value">{stats.aujourd_hui || 0}</div></div>
      </div>

      <div className="section-toolbar" style={{ flexDirection: 'column', gap: 12, alignItems: 'stretch' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="gss-input" placeholder="Rechercher..." value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} style={{ maxWidth: 200 }} />
          <select className="gss-input" value={filters.type_incident} onChange={e => setFilters(f => ({ ...f, type_incident: e.target.value }))} style={{ maxWidth: 160 }}>
            <option value="">Tous types</option>
            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select className="gss-input" value={filters.gravite} onChange={e => setFilters(f => ({ ...f, gravite: e.target.value }))} style={{ maxWidth: 140 }}>
            <option value="">Toutes gravites</option>
            {GRAVITES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
          <select className="gss-input" value={filters.statut} onChange={e => setFilters(f => ({ ...f, statut: e.target.value }))} style={{ maxWidth: 140 }}>
            <option value="">Tous statuts</option>
            {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="gss-input" value={filters.site_id} onChange={e => setFilters(f => ({ ...f, site_id: e.target.value }))} style={{ maxWidth: 180 }}>
            <option value="">Tous sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label className="form-label" style={{ margin: 0, fontSize: 13 }}>Du</label>
            <input type="date" className="gss-input" value={filters.date_debut}
              onChange={e => setFilters(f => ({ ...f, date_debut: e.target.value }))} />
            <label className="form-label" style={{ margin: 0, fontSize: 13 }}>Au</label>
            <input type="date" className="gss-input" value={filters.date_fin}
              onChange={e => setFilters(f => ({ ...f, date_fin: e.target.value }))} />
          </div>
          {canAction(roles, 'incidents', 'create') && (
            <button
              className="gss-btn"
              style={{ background: '#DC2626', color: '#fff', fontWeight: 700, fontSize: 14, padding: '10px 20px', borderRadius: 8 }}
              onClick={() => setShowForm(true)}
            >
              SIGNALER INCIDENT
            </button>
          )}
        </div>
      </div>

      {/* FORMULAIRE SIGNALEMENT — MODAL */}
      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <h2 style={{ color: '#DC2626' }}>Signaler un incident</h2>
              </div>
              <div className="gss-modal-subtitle">Declaration d'incident de securite</div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div className="gss-form-grid">
                <div className="gss-modal-section"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg><span>CLASSIFICATION INCIDENT</span></div>
                <div className="gss-field">
                  <label>Type d'incident *</label>
                  <select className="gss-input" value={form.type_incident} onChange={e => setForm(f => ({ ...f, type_incident: e.target.value }))}>
                    <option value="">-- Selectionner --</option>
                    {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="gss-field">
                  <label>Gravite</label>
                  <select className="gss-input" value={form.gravite} onChange={e => setForm(f => ({ ...f, gravite: e.target.value }))}>
                    {GRAVITES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
                <div className="gss-field">
                  <label>Site</label>
                  <select className="gss-input" value={form.site_id} onChange={e => setForm(f => ({ ...f, site_id: e.target.value }))}>
                    <option value="">-- Selectionner --</option>
                    {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                  </select>
                </div>
                <div className="gss-field">
                  <label>Agent declarant</label>
                  <select className="gss-input" value={form.agent_id} onChange={e => setForm(f => ({ ...f, agent_id: e.target.value }))}>
                    <option value="">-- Selectionner --</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>)}
                  </select>
                </div>
                <div className="gss-modal-section"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg><span>DETAILS & PREUVES</span></div>
                <div className="gss-field gss-field-full">
                  <label>Description courte *</label>
                  <textarea className="gss-input" rows={3} value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Decrivez brievement l'incident..." />
                </div>
                <div className="gss-field gss-field-full">
                  <label>Mesures prises</label>
                  <textarea className="gss-input" rows={2} value={form.mesures_prises}
                    onChange={e => setForm(f => ({ ...f, mesures_prises: e.target.value }))}
                    placeholder="Quelles mesures ont ete prises immediatement ?" />
                </div>
                <div className="gss-field">
                  <label>Photos (optionnel)</label>
                  {/* ⚠️ LE CAS LE PLUS CRITIQUE : photos prises AU TÉLÉPHONE, SUR LE
                      TERRAIN, en 3G, plusieurs à la fois. Sans redimensionnement,
                      4 photos de 4 Mo = 16 Mo à envoyer depuis un site de
                      gardiennage. Chacune est réduite à 1024 px avant l'envoi.
                      `accept` vient de la table, plus de `image/*` qui laissait
                      passer des HEIC que le serveur refuse ensuite. */}
                  <input type="file" accept=".jpg,.jpeg,.png" multiple
                    onChange={choisirPhotos} className="gss-input" />
                  {erreurPhotos && (
                    <div style={{ fontSize: 11, color: 'var(--gss-danger)', marginTop: 4 }}>{erreurPhotos}</div>
                  )}
                </div>
                <div className="gss-field" style={{ display: 'flex', alignItems: 'end', paddingBottom: '0.55rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    padding: '8px 16px', borderRadius: 8,
                    background: form.police_alertee ? '#FEE2E2' : '#F3F4F6',
                    border: `1px solid ${form.police_alertee ? '#EF4444' : '#D1D5DB'}`,
                  }}>
                    <input type="checkbox" checked={form.police_alertee}
                      onChange={e => setForm(f => ({ ...f, police_alertee: e.target.checked }))} />
                    <strong>Police alertee ?</strong>
                  </label>
                </div>
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
              <button className="gss-btn-deploy" onClick={submit} disabled={loading}
                style={{ background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                {loading ? 'Envoi...' : 'ENVOYER LE SIGNALEMENT'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DETAIL INCIDENT — Modal */}
      {detail && (
        <div className="gss-modal-overlay" onClick={() => setDetail(null)}>
          <div className="gss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Incident {detail.numero}</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="gss-btn-sm" onClick={() => telechargerPDF(detail.id)}>Export PDF</button>
                <button className="gss-btn secondary" onClick={() => setDetail(null)}>Fermer</button>
              </div>
            </div>
            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div><strong>Type :</strong> {TYPES.find(t => t.value === detail.type_incident)?.label || detail.type_incident}</div>
              <div><strong>Gravite :</strong>{' '}
                <span className={`gss-badge ${graviteInfo(detail.gravite).badge}`}
                  style={{ background: graviteInfo(detail.gravite).color, color: '#fff' }}>
                  {detail.gravite?.toUpperCase()}
                </span>
              </div>
              <div><strong>Date :</strong> {new Date(detail.date_incident).toLocaleString('fr-FR')}</div>
              <div><strong>Statut :</strong> <span className={`gss-badge ${statutBadge(detail.statut)}`}>{detail.statut}</span></div>
              {detail.site_nom && <div><strong>Site :</strong> {detail.site_nom}</div>}
              {detail.agent_nom && <div><strong>Agent :</strong> {detail.agent_prenom} {detail.agent_nom} ({detail.matricule})</div>}
              <div><strong>Police alertee :</strong>{' '}
                {detail.police_alertee
                  ? <span style={{ color: '#DC2626', fontWeight: 600 }}>OUI</span>
                  : 'NON'}
              </div>
              {detail.cree_par_nom && <div><strong>Declare par :</strong> {detail.cree_par_nom}</div>}
            </div>
            <div style={{ marginTop: 12 }}>
              <strong>Description :</strong>
              <div style={{ padding: 8, background: '#F9FAFB', borderRadius: 6, marginTop: 4, whiteSpace: 'pre-wrap' }}>{detail.description}</div>
            </div>
            {detail.mesures_prises && (
              <div style={{ marginTop: 8 }}>
                <strong>Mesures prises :</strong>
                <div style={{ padding: 8, background: '#F0FDF4', borderRadius: 6, marginTop: 4, whiteSpace: 'pre-wrap' }}>{detail.mesures_prises}</div>
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <IAAnalysisPanel type="incident" buttonLabel="Analyser l'incident" data={{
                type: detail.type_incident, gravite: detail.gravite, description: detail.description,
                site: detail.site_nom, mesures_prises: detail.mesures_prises, statut: detail.statut,
              }} />
            </div>
            {detail.photos && Array.isArray(detail.photos) && detail.photos.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <strong>Photos :</strong>
                <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                  {detail.photos.map((p, i) => (
                    <ImageProtegee key={i} src={urlDocument('photo-incident', detail.id, i)}
                      alt={`Piece jointe ${i + 1}`}
                      style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 6 }} />
                  ))}
                </div>
              </div>
            )}
            {canAction(roles, 'incidents', 'update') && detail.statut !== 'clos' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                {detail.statut === 'ouvert' && (
                  <button className="gss-btn warning" onClick={() => updateStatut(detail.id, 'en_cours')}>Prendre en charge</button>
                )}
                <button className="gss-btn success" onClick={() => updateStatut(detail.id, 'clos')}>Cloturer</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TABLEAU */}
      <div className="gss-table-wrapper">
        <table className="gss-table">
          <thead>
            <tr>
              <th>N°</th>
              <th>Date</th>
              <th>Type</th>
              <th>Gravite</th>
              <th>Site</th>
              <th>Agent</th>
              <th>Police</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {incidents.length === 0 && (
              <tr><td colSpan="9" style={{ padding: 0, border: 'none' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucun incident enregistre</div>
                  <div style={{ fontSize: 14, color: '#999' }}>Cliquez sur le bouton pour commencer</div>
                </div>
              </td></tr>
            )}
            {incidents.map(inc => (
              <tr key={inc.id}>
                <td><span className="mono">{inc.numero}</span></td>
                <td>{new Date(inc.date_incident).toLocaleDateString('fr-FR')}</td>
                <td>{inc.type_incident}</td>
                <td>
                  <span className="gss-badge" style={{
                    background: graviteInfo(inc.gravite).color,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 4,
                  }}>
                    {inc.gravite?.toUpperCase()}
                  </span>
                </td>
                <td>{inc.site_nom || '-'}</td>
                <td>{inc.agent_prenom ? `${inc.agent_prenom} ${inc.agent_nom}` : '-'}</td>
                <td>{inc.police_alertee ? <span style={{ color: '#DC2626', fontWeight: 600 }}>OUI</span> : 'Non'}</td>
                <td><span className={`gss-badge ${statutBadge(inc.statut)}`}>{inc.statut}</span></td>
                <td style={{ display: 'flex', gap: 4 }}>
                  <button className="gss-btn-icon" onClick={() => voirDetail(inc.id)} title="Detail">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  </button>
                  <button className="gss-btn-icon" onClick={() => telechargerPDF(inc.id)} title="PDF">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
